import React from 'react';
import {
  useCoordinateMemo,
  useCursorStationary,
  useDrawCrosshair,
  useLayers,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { Card, CardHeader, CardTitle } from '@ais-ui/ui-core';
import {
  AtonTargetInfo,
  BaseStationTargetInfo,
  EnvironmentalTargetInfo,
  getTargetInfoName,
  MethydTargetInfo,
  RadarTargetInfo,
  ShipInfo,
  TargetInfoType,
  TargetTypeEnum,
  useTargetInfoAtPixel,
  VirtualAtonTargetInfo,
} from '@ais-ui/websockets';
import { useState } from 'react';
import { usePopper } from 'react-popper';
import { useAISMapContext } from './AISMapContext';
import { VesselInfoTooltip } from './HoverTargets/VesselInfoHover';
import { BaseStationInfoTooltip } from './HoverTargets/BaseStationInfoHover';
import { AtonInfoTooltip } from './HoverTargets/AtonInfoHover';
import { VirtualAtonInfoTooltip } from './HoverTargets/VirtualAtonInfoHover';
import { MethydInfoTooltip } from './HoverTargets/MethydInfoHover';
import { EnvironmentalInfoTooltip } from './HoverTargets/EnvironmentalInfoHover';
import { RadarInfoTooltip } from './HoverTargets/RadarInfoHover';
import { useViewMode, ViewMode } from '../../components/ViewModeProvider';

interface CursorPosition {
  x: number;
  y: number;
}

interface TargetInfoTooltipProps {
  targetInfo: TargetInfoType;
  targetType: TargetTypeEnum;
  cursorPosition: CursorPosition;
}

const TargetInfoTooltip = ({
  targetInfo,
  targetType,
  cursorPosition,
}: TargetInfoTooltipProps) => {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );

  const targetName = getTargetInfoName(targetInfo, targetType);
  const [virtualReference] = useState(() => ({
    getBoundingClientRect: () =>
      ({
        top: cursorPosition.y,
        left: cursorPosition.x,
        bottom: cursorPosition.y,
        right: cursorPosition.x,
        width: 0,
        height: 0,
      } as DOMRect),
  }));

  const { styles } = usePopper(virtualReference, popperElement, {
    strategy: 'fixed',
    placement: 'right',
  });

  return cursorPosition ? (
    <Card
      ref={setPopperElement}
      style={styles.popper}
      className="mx-6 p-0 min-w-[128px] border-l-2 border-l-primary"
    >
      <CardHeader className="p-2">
        <CardTitle className="text-sm">{targetName}</CardTitle>
      </CardHeader>
      {targetType === TargetTypeEnum.BaseStation ? (
        <BaseStationInfoTooltip
          baseStationInfo={targetInfo as BaseStationTargetInfo}
        />
      ) : targetType === TargetTypeEnum.Aton ? (
        <AtonInfoTooltip atonInfo={targetInfo as AtonTargetInfo} />
      ) : targetType === TargetTypeEnum.VirtualAton ? (
        <VirtualAtonInfoTooltip
          virtualAtonInfo={targetInfo as VirtualAtonTargetInfo}
        />
      ) : targetType === TargetTypeEnum.Methyd ? (
        <MethydInfoTooltip methydInfo={targetInfo as MethydTargetInfo} />
      ) : targetType === TargetTypeEnum.Environmental ? (
        <EnvironmentalInfoTooltip
          environmentalInfo={targetInfo as EnvironmentalTargetInfo}
        />
      ) : targetType === TargetTypeEnum.Radar ? (
        <RadarInfoTooltip radarInfo={targetInfo as RadarTargetInfo} />
      ) : (
        <VesselInfoTooltip vesselInfo={targetInfo as ShipInfo} />
      )}
    </Card>
  ) : null;
};

export const TargetHover = () => {
  const stationaryCoordinate = useCursorStationary(200);
  const stationaryPixels = useCoordinateMemo(stationaryCoordinate);
  const AISMapContext = useAISMapContext();
  const { viewMode } = useViewMode();
  const context = useOpenLayersContext();
  const { isShipLayerVisible } = useLayers();

  const { data: tooltipInfo } = useTargetInfoAtPixel(
    stationaryPixels,
    false,
    false,
    !AISMapContext.isClickingShip,
    viewMode === ViewMode.Replay
  );

  const latitude =
    tooltipInfo && 'latitude' in tooltipInfo.extractedTargetInfo[0]
      ? (tooltipInfo.extractedTargetInfo[0] as { latitude: number }).latitude
      : null;

  const longitude =
    tooltipInfo && 'longitude' in tooltipInfo.extractedTargetInfo[0]
      ? (tooltipInfo.extractedTargetInfo[0] as { longitude: number }).longitude
      : null;

  useDrawCrosshair(latitude, longitude, context.map);

  if (!tooltipInfo || !stationaryPixels || !isShipLayerVisible) {
    return null;
  }

  const mapElement = context.map.getTargetElement();
  if (!mapElement) {
    return null;
  }

  const mapElementClientRect = mapElement.getBoundingClientRect();

  return (
    <TargetInfoTooltip
      targetInfo={tooltipInfo.extractedTargetInfo[0]}
      targetType={tooltipInfo.extractedTargetInfoType}
      cursorPosition={{
        x: stationaryPixels.x + mapElementClientRect.left,
        y: stationaryPixels.y + mapElementClientRect.top,
      }}
    />
  );
};
